import React, { useRef, useState } from 'react';
import { Portal, Dialog, TextInput, Button, Text, useTheme, RadioButton } from 'react-native-paper';
import { API_ENDPOINTS } from '../api/config';
import { apiFetch } from '../api/apiService';
import { View, Platform } from 'react-native';
import { CountryCodeSelector } from './CountryCodeSelector';
import { CustomDatePicker } from './CustomDatePicker';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useLicenseSafe } from '../hooks/useLicenseSafe';

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onSuccess?: () => void;
}

type LicensePayer = 'company' | 'self';

interface DriverInvitationPayload {
  email: string;
  license_payer: LicensePayer;
  full_name?: string;
  phone_number?: string;
  address?: string;
  license_number?: string;
  date_of_birth?: string;
}

// Licensing mask helper: same logic as in driver.tsx
const applyMaskLicenseNumber = (value: string): string => {
  let cleaned = value.replace(/[^0-9\/]/g, '');
  const slashCount = (cleaned.match(/\//g) || []).length;
  if (slashCount > 1) {
    const lastSlashIndex = cleaned.lastIndexOf('/');
    cleaned = cleaned.substring(0, lastSlashIndex);
  }
  const parts = cleaned.split('/');
  let numberPart = parts[0].replace(/\D/g, '').substring(0, 8);
  let yearPart = parts[1] !== undefined ? parts[1].replace(/\D/g, '').substring(0, 4) : '';
  if (numberPart.length === 8 && !cleaned.includes('/')) {
    return `${numberPart}/`;
  }
  if (parts.length > 1) {
    return `${numberPart}/${yearPart}`;
  }
  return numberPart;
};

export const InviteDriverModal: React.FC<Props> = ({ visible, onDismiss, onSuccess }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const [activateDialogVisible, setActivateDialogVisible] = useState(false);
  const [inactiveEmploymentId, setInactiveEmploymentId] = useState<number | null>(null);
  const [activateError, setActivateError] = useState('');
  const [activateLoading, setActivateLoading] = useState(false);
  // Reset all form fields to defaults
  const resetForm = () => {
    setEmail('');
    setLicensePayer(null);
    setFullName('');
    setPhoneNumber('');
    setAddress('');
    setLicenseNumber('');
    setDateOfBirth('');
    setShowOptional(false);
    setCountryCode('+351');
    setError('');
  };
  const [email, setEmail] = useState('');
  const isEmailValid = (val: string) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(val);
  const [licensePayer, setLicensePayer] = useState<LicensePayer | null>(null);
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const [showOptional, setShowOptional] = useState(false);
  const [countryCode, setCountryCode] = useState('+351');
  const [modalCountryVisible, setModalCountryVisible] = useState(false);
  
  // Use safe hook that works both inside and outside LicenseProvider
  const { licenseInfo, isLoading: licensesLoading, error: licensesError } = useLicenseSafe(visible);
  const availableLicenses = licenseInfo?.summary?.available_licenses;
  const hasKnownCompanyCapacity =
    !licensesLoading
    && !licensesError
    && typeof availableLicenses === 'number'
    && availableLicenses > 0;
  const submitInFlightRef = useRef(false);
  const activateInFlightRef = useRef(false);

  // Self-pay does not depend on corporate capacity. Company-pay is fail-closed
  // until the authoritative capacity query is complete and positive.
  const isInviteDisabled =
    loading
    || !email
    || !isEmailValid(email)
    || !licensePayer
    || (licensePayer === 'company' && !hasKnownCompanyCapacity);

  const handleSubmit = async () => {
    if (submitInFlightRef.current || isInviteDisabled || !licensePayer) {
      return;
    }

    submitInFlightRef.current = true;
    setLoading(true);
    setError('');
    const payload: DriverInvitationPayload = { email, license_payer: licensePayer };
    if (fullName) payload.full_name = fullName;
    if (phoneNumber) payload.phone_number = `${countryCode}${phoneNumber}`;
    if (address) payload.address = address;
    if (licenseNumber) payload.license_number = licenseNumber;
    if (dateOfBirth) payload.date_of_birth = dateOfBirth;
    try {
      const response = await apiFetch(
        `${API_ENDPOINTS.COMPANIES}me/drivers/invite/`,
        { method: 'POST', body: JSON.stringify(payload) }
      );
      if (!response.ok) {
        const data = response.data as any;
        if (licensePayer === 'company' && data?.inactive_employment && data?.employment_id) {
          // Preserve the existing explicit owner recovery only for company payer.
          setInactiveEmploymentId(data.employment_id);
          setActivateError('');
          setActivateDialogVisible(true);
        } else {
          const msg = data?.detail || JSON.stringify(data) || 'Error sending invitation';
          setError(msg);
        }
      } else {
        // Clear form, then close and notify parent
        resetForm();
        onDismiss();
        onSuccess && onSuccess();
      }
    } catch (e: any) {
      setError(e.message || 'Network error');
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleActivateConfirm = async () => {
    if (!inactiveEmploymentId || activateLoading || activateInFlightRef.current) return;
    activateInFlightRef.current = true;
    setActivateLoading(true);
    setActivateError('');
    try {
      const response = await apiFetch(
        `${API_ENDPOINTS.COMPANIES}${inactiveEmploymentId}/me/drivers/`,
        { method: 'PATCH', body: JSON.stringify({ is_active: true }) }
      );
      if (!response.ok) {
        const data = response.data as any;
        const detail = typeof data?.detail === 'string' ? data.detail.trim() : '';
        setActivateError(detail || t('inviteDriverModal.activateError'));
        return;
      }

      // Only a confirmed successful activation may reset and close the flow.
      resetForm();
      setActivateDialogVisible(false);
      onDismiss();
      onSuccess && onSuccess();
    } catch {
      setActivateError(t('inviteDriverModal.activateError'));
    } finally {
      activateInFlightRef.current = false;
      setActivateLoading(false);
    }
  };

  return (
    <Portal>
      <Dialog 
        visible={visible} 
        onDismiss={onDismiss}
        style={[
          { alignSelf: 'center' },
          Platform.OS === 'web' ? { maxWidth: 680, width: '90%' } : {}
        ]}
      >
        <Dialog.Title>{t('inviteForm.title')}</Dialog.Title>
        <Dialog.Content>
          {error ? <Text style={{ color: theme.colors.error, marginBottom: 8 }}>{error}</Text> : null}
          <Text variant="titleSmall" style={{ marginBottom: 4 }}>
            {t('inviteForm.licensePayerTitle')}
          </Text>
          <RadioButton.Group
            value={licensePayer ?? ''}
            onValueChange={(value) => setLicensePayer(value as LicensePayer)}
          >
            <RadioButton.Item
              testID="invite-payer-company"
              label={t('inviteForm.licensePayerCompany')}
              value="company"
              status={licensePayer === 'company' ? 'checked' : 'unchecked'}
              disabled={loading}
              onPress={() => setLicensePayer('company')}
            />
            <RadioButton.Item
              testID="invite-payer-self"
              label={t('inviteForm.licensePayerSelf')}
              value="self"
              status={licensePayer === 'self' ? 'checked' : 'unchecked'}
              disabled={loading}
              onPress={() => setLicensePayer('self')}
            />
          </RadioButton.Group>
          {!licensePayer ? (
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
              {t('inviteForm.licensePayerRequired')}
            </Text>
          ) : null}
          {licensePayer === 'company' && licensesLoading ? (
            <Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
              {t('inviteForm.companyLicenseLoading')}
            </Text>
          ) : null}
          {licensePayer === 'company' && !licensesLoading && licensesError ? (
            <Text style={{ color: theme.colors.error, marginBottom: 8 }}>
              {licensesError || t('inviteForm.companyLicenseError')}
            </Text>
          ) : null}
          {licensePayer === 'company' && !licensesLoading && !licensesError && typeof availableLicenses !== 'number' ? (
            <Text style={{ color: theme.colors.error, marginBottom: 8 }}>
              {t('inviteForm.companyLicenseUnavailable')}
            </Text>
          ) : null}
          {licensePayer === 'company' && !licensesLoading && !licensesError && availableLicenses === 0 ? (
            <Text style={{ color: theme.colors.error, marginBottom: 8 }}>
              {t('inviteForm.noLicensesAvailable')}
            </Text>
          ) : null}
          {licensePayer === 'company' && !licensesLoading && !licensesError && availableLicenses === 0 && (
            <Button
              testID="invite-buy-licenses-link"
              mode="text"
              onPress={() => {
                router.push('/dashboard/manage-licenses');
                onDismiss();
              }}
              style={{ marginBottom: 8 }}
            >
              {t('inviteForm.buyLicensesLink')}
            </Button>
          )}
          <Text 
            variant="bodySmall" 
            style={{ 
              color: theme.colors.onSurfaceVariant, 
              marginBottom: 12,
              fontStyle: 'italic'
            }}
          >
            {t('inviteForm.expirationInfo', 'This invitation will expire in 7 days.')}
          </Text>
          <TextInput
            testID="invite-email-input"
            mode="outlined"
            label={t('inviteForm.email')}
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            error={!!email && !isEmailValid(email)}
            style={{ marginBottom: 4 }}
          />
          {!!email && !isEmailValid(email) && (
            <Text style={{ color: theme.colors.error, marginBottom: 8 }}>
              {t('inviteForm.emailInvalid', 'Invalid email format')}
            </Text>
          )}
          {showOptional && (
            <>
              <TextInput
                mode="outlined"
                label={t('inviteForm.fullName')}
                value={fullName}
                onChangeText={setFullName}
                style={{ marginBottom: 8 }}
              />
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <Button mode="outlined" onPress={() => setModalCountryVisible(true)}>
                  {countryCode}
                </Button>
                <TextInput
                  mode="outlined"
                  label={t('inviteForm.phoneNumber')}
                  value={phoneNumber}
                  onChangeText={setPhoneNumber}
                  keyboardType="phone-pad"
                  style={{ flex: 1, marginLeft: 8 }}
                />
              </View>
              <CountryCodeSelector
                visible={modalCountryVisible}
                onDismiss={() => setModalCountryVisible(false)}
                onSelect={(code) => {
                  setCountryCode(code);
                  setModalCountryVisible(false);
                }}
                selectedCode={countryCode}
              />
              <TextInput
                mode="outlined"
                label={t('inviteForm.address')}
                value={address}
                onChangeText={setAddress}
                style={{ marginBottom: 8 }}
              />
              <View style={{ marginBottom: 8 }}>
                <TextInput
                  mode="outlined"
                  label={t('inviteForm.licenseNumber')}
                  value={licenseNumber}
                  onChangeText={(text) => {
                    const formatted = applyMaskLicenseNumber(text);
                    setLicenseNumber(formatted);
                  }}
                  error={!!licenseNumber && !/^[0-9]{1,8}\/[0-9]{4}$/.test(licenseNumber)}
                  style={{ marginBottom: 4 }}
                />
                {licenseNumber && !/^[0-9]{1,8}\/[0-9]{4}$/.test(licenseNumber) && (
                  <Text style={{ color: theme.colors.error, fontSize: 12 }}>
                    {t('inviteForm.licenseNumberInvalid')}
                  </Text>
                )}
              </View>
              <View style={{ marginBottom: 8 }}>
                <CustomDatePicker
                  label={t('inviteForm.dateOfBirth')}
                  value={dateOfBirth}
                  onChange={setDateOfBirth}
                  maxDate={new Date().toISOString().split('T')[0]}
                />
              </View>
            </>
          )}
        </Dialog.Content>
        <Dialog.Actions style={{ justifyContent: 'space-between', paddingHorizontal: 16 }}>
          {!showOptional ? (
            <>
              <Button 
                onPress={() => setShowOptional(true)} 
                mode="text" 
                style={{ flex: 1, justifyContent: 'flex-start' }}
                contentStyle={{ alignItems: 'flex-start' }}
              >
                {t('inviteForm.addMoreInfo')}
              </Button>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Button onPress={onDismiss}>{t('inviteForm.cancel')}</Button>
                <Button testID="invite-submit" onPress={handleSubmit} loading={loading} disabled={isInviteDisabled}>
                  {t('inviteForm.submit')}
                </Button>
              </View>
            </>
          ) : (
            <>
              <Button 
                onPress={() => setShowOptional(false)} 
                mode="text" 
                style={{ flex: 1, justifyContent: 'flex-start' }}
                contentStyle={{ alignItems: 'flex-start' }}
              >
                {t('inviteForm.back')}
              </Button>
              <Button testID="invite-submit" onPress={handleSubmit} loading={loading} disabled={isInviteDisabled}>
                {t('inviteForm.submit')}
              </Button>
            </>
          )}
        </Dialog.Actions>
      </Dialog>
      <Dialog 
        testID="invite-activate-dialog"
        visible={activateDialogVisible} 
        onDismiss={() => setActivateDialogVisible(false)}
        style={[
          { alignSelf: 'center' },
          Platform.OS === 'web' ? { maxWidth: 520, width: '90%' } : {}
        ]}
      >
        <Dialog.Title>{t('inviteDriverModal.activateDialogTitle')}</Dialog.Title>
        <Dialog.Content>
          <Text>{t('inviteDriverModal.activateDialogMessage')}</Text>
          {activateError ? (
            <Text style={{ color: theme.colors.error, marginTop: 8 }}>{activateError}</Text>
          ) : null}
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={() => setActivateDialogVisible(false)}>{t('common.cancel')}</Button>
          <Button onPress={handleActivateConfirm} loading={activateLoading} disabled={activateLoading}>
            {t('inviteDriverModal.activate')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
}; 